import React, { createContext } from 'react';
import { logError } from '@utils/usefulFuncs';
import { QuerySeriesContext } from '@/contexts/QuerySeriesContext';

const defaultState = {
  activeRowIndex: null,
  activeRow: null,
  messages: [],
  isStreaming: false,
  error: null,
  openChat: () => logError('openChat not initialized'),
  closeChat: () => logError('closeChat not initialized'),
  addUserMessage: () => logError('addUserMessage not initialized'),
  addAssistantMessage: () => logError('addAssistantMessage not initialized'),
  updateLastAssistantMessage: () => logError('updateLastAssistantMessage not initialized'),
  finalizeLastAssistantMessage: () => logError('finalizeLastAssistantMessage not initialized'),
  setIsStreaming: () => logError('setIsStreaming not initialized'),
  setError: () => logError('setError not initialized'),
  replaceRow: () => logError('replaceRow not initialized'),
};

export const RowChatContext = createContext(defaultState);

export const RowChatProvider = ({ children }) => {
  const { rows, updateRow } = React.useContext(QuerySeriesContext);
  const [activeRowIndex, setActiveRowIndex] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [error, setError] = React.useState(null);

  const activeRow = React.useMemo(() => {
    if (activeRowIndex === null) return null;
    return rows.find((r) => r.rowIndex === activeRowIndex) || null;
  }, [rows, activeRowIndex]);

  const openChat = React.useCallback(
    (rowIndex) => {
      const row = rows.find((r) => r.rowIndex === rowIndex);
      if (!row) return;
      setActiveRowIndex(rowIndex);
      setMessages([
        {
          role: 'assistant',
          text: '',
          answer: row.answer || '',
          reasoning: row.reasoning || '',
          isOriginal: true,
        },
      ]);
      setError(null);
      setIsStreaming(false);
    },
    [rows]
  );

  const closeChat = React.useCallback(() => {
    setActiveRowIndex(null);
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  const addUserMessage = React.useCallback((text) => {
    setMessages((prev) => [...prev, { role: 'user', text }]);
  }, []);

  const addAssistantMessage = React.useCallback(() => {
    setMessages((prev) => [...prev, { role: 'assistant', text: '', answer: '', reasoning: '' }]);
  }, []);

  const updateLastAssistantMessage = React.useCallback((rawText, parsed) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = {
          ...last,
          text: rawText,
          answer: parsed?.answer || last.answer,
          reasoning: parsed?.reasoning || last.reasoning,
        };
      }
      return next;
    });
  }, []);

  const finalizeLastAssistantMessage = React.useCallback((answer, reasoning) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = { ...last, answer: answer || last.answer, reasoning: reasoning || last.reasoning };
      }
      return next;
    });
  }, []);

  const replaceRow = React.useCallback(() => {
    if (activeRowIndex === null) return;
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant' && !m.isOriginal);
    if (!lastAssistant) return;
    updateRow(activeRowIndex, lastAssistant.answer || '', lastAssistant.reasoning || '');
    setActiveRowIndex(null);
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  }, [activeRowIndex, messages, updateRow]);

  const value = React.useMemo(
    () => ({
      activeRowIndex,
      activeRow,
      messages,
      isStreaming,
      error,
      openChat,
      closeChat,
      addUserMessage,
      addAssistantMessage,
      updateLastAssistantMessage,
      finalizeLastAssistantMessage,
      setIsStreaming,
      setError,
      replaceRow,
    }),
    [
      activeRowIndex,
      activeRow,
      messages,
      isStreaming,
      error,
      openChat,
      closeChat,
      addUserMessage,
      addAssistantMessage,
      updateLastAssistantMessage,
      finalizeLastAssistantMessage,
      replaceRow,
    ]
  );

  return <RowChatContext.Provider value={value}>{children}</RowChatContext.Provider>;
};
